import { Offcanvas, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
  defaultType?: "LOGO" | "FILE"; // ✅ passed from parent
}

type FormData = {
  title: string;
  file: string;
  type: "LOGO" | "FILE";
};

const AddMediaKitPresentation = ({
  show,
  handleClose,
  data,
  defaultType,
}: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const selectedType = watch("type");

  useEffect(() => {
    if (show) {
      reset({
        type: data?.type || defaultType || "LOGO",
        title: data?.title || "",
      });
    } else {
      reset({});
      setFile(null);
    }
  }, [show, data, reset, defaultType]);

  const onSubmit = (request: FormData) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("title", request.title);
    formData.append("type", request.type);

    if (file) {
      formData.append("file", file);
    }

    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
        action: `media-kit/edit-media-kit/${data._id}`,
        body: formData,
      })
      : WebService.fileUploadAPI({
        action: `media-kit/add-media-kit`,
        body: formData,
      });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        setFile(null);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        setFile(null);
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{data ? "Edit Media" : "Add Media"}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          {/* Type field - locked in edit mode */}
          <Form.Group className="mb-2" controlId="mediaType">
            <Form.Label className="label">Type<span className="text-danger"> *</span></Form.Label>
            <Form.Select
              {...register("type", { required: true })}
              disabled={!!data} // ✅ lock type when editing
            >
              <option value="LOGO">Logo</option>
              <option value="FILE">File</option>
            </Form.Select>
            {errors.type && <p className="text-danger">Type is required</p>}
          </Form.Group>

          <Form.Group className="mb-2" controlId="mediaTitle">
            <Form.Label className="label">Title<span className="text-danger"> *</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter title"
              {...register("title", { required: true })}
            />
            {errors.title && <p className="text-danger">Title is required</p>}
          </Form.Group>

          {/* File Upload */}
          <Form.Group className="mb-2" controlId="eventFile">
            <Form.Label className="label">
              {selectedType === "LOGO" ? "Upload Image" : "Upload File"}<span className="text-danger"> *</span>
            </Form.Label>
            <Form.Control
              type="file"
              accept={selectedType === "LOGO" ? "image/png,image/jpg,image/jpeg,image/svg+xml" : ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"}
              className="custom-file-upload"
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                const selectedFile = input.files && input.files[0];
                if (selectedFile) {
                  setFile(selectedFile);
                }
              }}
            />
            <div className="text-primary mt-1" style={{ fontSize: "13px" }}>
              Note: Please upload a file or image of size: <strong>130 × 160 px</strong> or <strong>5MB</strong>.
            </div>
          </Form.Group>

          {/* Preview */}
          {(file || data?.file) && (
            <div className="mb-2 event-file">
              {selectedType === "LOGO" || data?.type === "LOGO" ? (
                <img
                  src={file ? URL.createObjectURL(file) : data?.file}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                  }}
                />
              ) : (
                <a
                  href={file ? URL.createObjectURL(file) : data?.file}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View File
                </a>
              )}
            </div>
          )}
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddMediaKitPresentation;
